import { useCallback, useState, type DragEvent, type ReactNode } from "react";
import { Caret } from "./Caret";
import {
  DEFAULT_ZONE_ORDER,
  clearNavOrder,
  loadNavOrder,
  mergeNavOrder,
  moveNavItem,
  saveNavOrder,
  type NavZoneId,
} from "../lib/navOrder";

export function SidebarNavRow({
  label,
  active,
  onClick,
  dotDanger,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  /**
   * track:track-backlog--attention-badge-actionable-not-just-count — was a
   * numeric badge that didn't link anywhere near what it counted, so it
   * read as decorative rather than actionable. Replaced with a plain status
   * dot: red when the Actions badge count is above zero (same count as the
   * Control Center tab-bar badge), a neutral color when false. Undefined renders no dot at all — only Control Center passes
   * this today.
   */
  dotDanger?: boolean;
}) {
  return (
    <button
      type="button"
      className={`sb-row sb-nav-row${active ? " active" : ""}`}
      onClick={onClick}
    >
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </span>
      {dotDanger !== undefined && (
        <span
          aria-hidden="true"
          title={dotDanger ? "Action needed" : "No action needed"}
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: dotDanger ? "var(--red)" : "var(--line)",
            flexShrink: 0,
          }}
        />
      )}
    </button>
  );
}

export type NavRowDef = {
  id: string;
  label: string;
  active: boolean;
  onClick: () => void;
  visible: boolean;
  dotDanger?: boolean;
};

/** Per-browser order for the Always rows and the three zones (NAV-ORDER-A). */
export function useNavOrder(rows: readonly NavRowDef[]) {
  const [saved, setSaved] = useState(() => loadNavOrder());
  const visibleIds = rows.filter((r) => r.visible).map((r) => r.id);
  const alwaysOrder = mergeNavOrder(saved?.always, visibleIds);
  const zoneOrder = mergeNavOrder(saved?.zones, DEFAULT_ZONE_ORDER) as NavZoneId[];

  const setAlwaysOrder = useCallback(
    (always: string[]) => {
      const next = { zones: zoneOrder, always };
      saveNavOrder(next);
      setSaved({ version: 1, ...next });
    },
    [zoneOrder],
  );
  const setZoneOrder = useCallback(
    (zones: string[]) => {
      const next = { zones, always: alwaysOrder };
      saveNavOrder(next);
      setSaved({ version: 1, ...next });
    },
    [alwaysOrder],
  );
  const reset = useCallback(() => {
    clearNavOrder();
    setSaved(null);
  }, []);

  return { alwaysOrder, zoneOrder, setAlwaysOrder, setZoneOrder, reset };
}

export function NavResetButton({ onReset }: { onReset: () => void }) {
  return (
    <button type="button" className="sb-nav-reset" onClick={onReset}>
      Reset nav order
    </button>
  );
}

type Drag = { kind: "row" | "zone"; id: string };

function startDrag(e: DragEvent, id: string) {
  e.dataTransfer?.setData("text/plain", id);
  if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
}

function dropIndex(e: DragEvent, index: number): number {
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
  return e.clientY < rect.top + rect.height / 2 ? index : index + 1;
}

function Grip({ label, onDragStart, onDragEnd }: { label: string; onDragStart: (e: DragEvent) => void; onDragEnd: () => void }) {
  return (
    <button
      type="button"
      className="sb-grip"
      draggable
      aria-label={label}
      title="Drag to move"
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <span aria-hidden>⋮⋮</span>
    </button>
  );
}

function MoveButtons({ label, canUp, canDown, onUp, onDown }: { label: string; canUp: boolean; canDown: boolean; onUp: () => void; onDown: () => void }) {
  return (
    <span className="sb-move">
      <button type="button" aria-label={`Move ${label} up`} title="Move up" disabled={!canUp} onClick={onUp}>
        ▲
      </button>
      <button type="button" aria-label={`Move ${label} down`} title="Move down" disabled={!canDown} onClick={onDown}>
        ▼
      </button>
    </span>
  );
}

export function SidebarNav({
  rows,
  alwaysOrder,
  zoneOrder,
  onAlwaysOrder,
  onZoneOrder,
  alwaysCollapsed,
  onToggleAlways,
  projects,
  filters,
}: {
  rows: readonly NavRowDef[];
  alwaysOrder: string[];
  zoneOrder: NavZoneId[];
  onAlwaysOrder: (ids: string[]) => void;
  onZoneOrder: (ids: string[]) => void;
  alwaysCollapsed: boolean;
  onToggleAlways: () => void;
  projects: ReactNode;
  filters: ReactNode;
}) {
  const [drag, setDrag] = useState<Drag | null>(null);
  const [dropAt, setDropAt] = useState<{ kind: "row" | "zone"; index: number } | null>(null);
  const endDrag = () => {
    setDrag(null);
    setDropAt(null);
  };

  const rowById = new Map(rows.map((r) => [r.id, r]));
  const alwaysRows = alwaysOrder.map((id) => rowById.get(id)).filter((r): r is NavRowDef => !!r);

  const moveRow = (from: number, to: number) => onAlwaysOrder(moveNavItem(alwaysOrder, from, to));
  const moveZone = (from: number, to: number) => onZoneOrder(moveNavItem(zoneOrder, from, to));

  const renderAlways = () => (
    <>
      <button className="sb-summary" onClick={onToggleAlways} aria-expanded={!alwaysCollapsed}>
        <Caret open={!alwaysCollapsed} />
        Always
      </button>
      {!alwaysCollapsed && (
        <div
          className="sb-collapsible-body"
          data-testid="nav-always-list"
          style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 12 }}
        >
          {alwaysRows.map((row, i) => (
            <div
              key={row.id}
              className={`sb-nav-item${dropAt?.kind === "row" && dropAt.index === i ? " drop-before" : ""}${
                dropAt?.kind === "row" && dropAt.index === alwaysRows.length && i === alwaysRows.length - 1 ? " drop-after" : ""
              }${drag?.kind === "row" && drag.id === row.id ? " dragging" : ""}`}
              data-nav-id={row.id}
              onDragOver={(e) => {
                if (drag?.kind !== "row") return;
                e.preventDefault();
                setDropAt({ kind: "row", index: dropIndex(e, i) });
              }}
              onDrop={(e) => {
                if (drag?.kind !== "row") return;
                e.preventDefault();
                const from = alwaysOrder.indexOf(drag.id);
                if (from >= 0) moveRow(from, dropIndex(e, i));
                endDrag();
              }}
            >
              <Grip
                label={`Drag ${row.label} to reorder`}
                onDragStart={(e) => {
                  startDrag(e, row.id);
                  setDrag({ kind: "row", id: row.id });
                }}
                onDragEnd={endDrag}
              />
              <SidebarNavRow label={row.label} active={row.active} onClick={row.onClick} dotDanger={row.dotDanger} />
              <MoveButtons
                label={row.label}
                canUp={i > 0}
                canDown={i < alwaysRows.length - 1}
                onUp={() => moveRow(i, i - 1)}
                onDown={() => moveRow(i, i + 2)}
              />
            </div>
          ))}
        </div>
      )}
    </>
  );

  const zoneContent: Record<NavZoneId, ReactNode> = {
    always: renderAlways(),
    projects,
    filters,
  };
  const zoneLabel: Record<NavZoneId, string> = { always: "Always", projects: "Projects", filters: "Filters" };

  return (
    <>
      {zoneOrder.map((zone, i) => (
        <div
          key={zone}
          className={`sb-zone${dropAt?.kind === "zone" && dropAt.index === i ? " drop-before" : ""}${
            dropAt?.kind === "zone" && dropAt.index === zoneOrder.length && i === zoneOrder.length - 1 ? " drop-after" : ""
          }${drag?.kind === "zone" && drag.id === zone ? " dragging" : ""}`}
          data-zone-id={zone}
          onDragOver={(e) => {
            if (drag?.kind !== "zone") return;
            e.preventDefault();
            setDropAt({ kind: "zone", index: dropIndex(e, i) });
          }}
          onDrop={(e) => {
            if (drag?.kind !== "zone") return;
            e.preventDefault();
            const from = zoneOrder.indexOf(drag.id as NavZoneId);
            if (from >= 0) moveZone(from, dropIndex(e, i));
            endDrag();
          }}
        >
          <div className="sb-zone-tools">
            <Grip
              label={`Drag ${zoneLabel[zone]} section to reorder`}
              onDragStart={(e) => {
                startDrag(e, zone);
                setDrag({ kind: "zone", id: zone });
              }}
              onDragEnd={endDrag}
            />
            <MoveButtons
              label={`${zoneLabel[zone]} section`}
              canUp={i > 0}
              canDown={i < zoneOrder.length - 1}
              onUp={() => moveZone(i, i - 1)}
              onDown={() => moveZone(i, i + 2)}
            />
          </div>
          {zoneContent[zone]}
        </div>
      ))}
    </>
  );
}
